import React, { useState } from "react";
import { ChevronUp } from "lucide-react";
import { panelTokens } from "../config/graphThemes";
import { safeIcon } from "../config/graphIcons";

const LegendPanel = ({
  dark,
  t,
  typeCounts,
  activeTypeFilter,
  setActiveTypeFilter,
}) => {
  const tk = panelTokens(dark);
  const [open, setOpen] = useState(true);

  if (!typeCounts.length) return null;

  const panelBg =
    tk.innerGlow !== "none"
      ? `${tk.innerGlow}, ${t.surface.bgSolid}`
      : t.surface.bgSolid;

  return (
    <div
      className="absolute z-20 right-3 top-14 pointer-events-auto rounded-lg backdrop-blur-md overflow-hidden w-48"
      style={{
        background: panelBg,
        border: `1px solid ${tk.border}`,
        animation: "arwes-panel-pulse 5s ease-in-out infinite",
      }}
    >
      {/* Header */}
      <button
        className="relative z-[1] w-full flex items-center justify-between px-3 py-2 cursor-pointer"
        style={{ borderBottom: open ? `1px solid ${tk.divider}` : "none" }}
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[11px] uppercase tracking-[0.08em] font-semibold"
            style={{ color: tk.mutedText }}
          >
            Legend
          </span>
          {activeTypeFilter.size > 0 && (
            <span
              className="text-[10px] font-semibold"
              style={{
                color: `rgba(${tk.cyanLitRgb},${dark ? "0.85" : "1"})`,
                textShadow: dark ? `0 0 7px rgba(${tk.cyanRgb},0.50)` : "none",
              }}
            >
              {activeTypeFilter.size} active
            </span>
          )}
        </div>
        <ChevronUp
          size={13}
          style={{
            color: tk.mutedText,
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {/* Items */}
      {open && (
        <>
          <div
            className="overflow-y-auto py-1 relative z-[1]"
            style={{ maxHeight: "calc(50vh - 72px)", scrollbarWidth: "thin" }}
          >
            {typeCounts.map(([tp, count]) => {
              const isActive = activeTypeFilter.has(tp);
              const iconColor = t.node.typeColors?.[tp] || t.node.iconColor;
              const IconComp = safeIcon(tp);
              return (
                <button
                  key={tp}
                  className="flex items-center gap-2 text-left transition-all"
                  style={{
                    background: isActive ? tk.activeBg : "transparent",
                    border: `1px solid ${isActive ? `rgba(${tk.cyanRgb},0.22)` : "transparent"}`,
                    borderRadius: 6,
                    margin: "1px 4px",
                    padding: "5px 8px",
                    width: "calc(100% - 8px)",
                    boxShadow:
                      isActive && dark
                        ? `0 0 10px rgba(${tk.cyanRgb},0.12), inset 0 0 6px rgba(${tk.cyanRgb},0.04)`
                        : "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive)
                      e.currentTarget.style.background = tk.hoverBg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isActive
                      ? tk.activeBg
                      : "transparent";
                  }}
                  onClick={() =>
                    setActiveTypeFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(tp)) next.delete(tp);
                      else next.add(tp);
                      return next;
                    })
                  }
                >
                  {React.createElement(IconComp, {
                    size: 13,
                    style: {
                      color: isActive ? `rgb(${tk.cyanLitRgb})` : iconColor,
                      flexShrink: 0,
                      filter:
                        isActive && dark
                          ? `drop-shadow(0 0 3px rgba(${tk.cyanRgb},0.60))`
                          : "none",
                      transition: "color 0.15s, filter 0.15s",
                    },
                  })}
                  <span
                    className="truncate flex-1 text-[12px]"
                    style={{
                      color: isActive ? `rgb(${tk.cyanLitRgb})` : tk.mutedText,
                      textShadow:
                        isActive && dark
                          ? `0 0 7px rgba(${tk.cyanLitRgb},0.50)`
                          : "none",
                      transition: "color 0.15s, text-shadow 0.15s",
                    }}
                  >
                    {tp.replace(/_/g, " ").toLowerCase()}
                  </span>
                  <span
                    className="text-[11px] font-semibold flex-shrink-0 tabular-nums"
                    style={{
                      color: isActive
                        ? `rgba(${tk.cyanRgb},${dark ? "0.85" : "1"})`
                        : dark
                          ? "rgba(148,163,184,0.30)"
                          : "rgba(71,85,105,0.45)",
                      textShadow:
                        isActive && dark
                          ? `0 0 6px rgba(${tk.cyanRgb},0.45)`
                          : "none",
                      transition: "color 0.15s, text-shadow 0.15s",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Clear filter */}
          {activeTypeFilter.size > 0 && (
            <button
              className="relative z-[1] w-full text-center py-2 text-[11px] font-medium transition-all cursor-pointer"
              style={{
                borderTop: `1px solid ${tk.divider}`,
                color: dark ? "rgba(248,113,113,0.80)" : "rgba(220,38,38,0.80)",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = dark
                  ? "rgba(239,68,68,0.07)"
                  : "rgba(239,68,68,0.06)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              onClick={() => setActiveTypeFilter(new Set())}
            >
              Clear filter
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default LegendPanel;
